import React from 'react';
import {Form, Radio, DatePicker, Select, Divider, Checkbox, Input} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import GenericDialog from '../../../../common/GenericDialog';
import {HelpIcon} from '../../../../common/icons';
import dayjs from 'dayjs';
import TooltipMUI from '@mui/material/Tooltip';

// For brevity, define the "RECIPIENT" and "RECIPIENT_ARR" if you need them here
// or import them from your "enums" file. Also define your brandConfig or user context
// as needed. The same goes for "editAssignObj" if it’s constant or can be passed in.

const AssignDiagnosticTestDialog = ({
                              open,
                              onClose,
                              onAssignTests,
                              assignTestForm,
                              editAssignObj,
                              recipientState,
                              classIds,
                              studentIds,
                              students,
                              initialStudentIds,
                              initialClassIds,
                              user,
                              openDialogDate,
                              startDate,
                              onChangeStartDate,
                              onFormatChange,
                              onRecipientChange,
                              onClassChange,
                              handleClassChange,
                              handleStudentChange,
                              handleSelectAllClassIds,
                              handleSelectAllStudentIds,
                              initialRecipientValue = null
                          }) => {

    return (
        <GenericDialog
            title={'Assign Test'}
            open={open}
            contentStyle={{width: 600}}
            onClose={onClose}
            buttons={[
                {
                    name: 'Cancel',
                    extraProps: {type: 'text'},
                },
                {
                    name: 'Assign',
                    callback: onAssignTests,
                },
            ]}
        >
            <div className={'dialog-form-tooltip-wrapper'} style={{left: 220, top: 50}}>
                <TooltipMUI placement={'top'} title={editAssignObj.START_END_DATE_TOOLTIP}>
                  <span>
                    <HelpIcon
                        sx={{m: 1}}
                        style={{
                            verticalAlign: 'middle',
                            width: '16px',
                            height: '16px',
                        }}
                    />
                  </span>
                </TooltipMUI>
            </div>
            <div className={'dialog-form-tooltip-wrapper'} style={{left: 220, top: 138}}>
                <TooltipMUI placement={'top'} title={editAssignObj.SHOW_RESULTS_ON_EXPIRY_TOOLTIP}>
          <span>
            <HelpIcon
                sx={{m: 1}}
                style={{
                    verticalAlign: 'middle',
                    width: '16px',
                    height: '16px',
                }}
            />
          </span>
                </TooltipMUI>
            </div>

            <Form
                className={'testFamilyForm editAssignTestForm'}
                form={assignTestForm}
                labelCol={{span: 9}}
                wrapperCol={{span: 15}}
                initialValues={{
                    format: editAssignObj.formatType.ASSESSMENT.val,
                    messageForStudents: '',
                    recipient: initialRecipientValue !== null ? initialRecipientValue : recipientState.value,
                    showResultsOnExpiry: false,
                    dateRange: [dayjs()],
                }}
            >
                <Form.Item label={'Choose format'} name={'format'}>
                    <Radio.Group
                        optionType="button"
                        buttonStyle='solid'
                        onChange={onFormatChange}
                        options={[
                            {
                                label: editAssignObj.formatType.ASSESSMENT.name,
                                value: editAssignObj.formatType.ASSESSMENT.val,
                            },
                            {
                                label: editAssignObj.formatType.REVISION.name,
                                value: editAssignObj.formatType.REVISION.val,
                            },
                        ]}
                    />
                </Form.Item>
                <Form.Item
                    label={'Start date'}
                    name={'startDate'}
                    rules={[
                        {
                            required: true,
                            message: 'Please select a start date.',
                        },
                        {
                            validator: (_, value) => {
                                if (openDialogDate && value && value < openDialogDate) {
                                    return Promise.reject(
                                        `Start date must not be before (${openDialogDate.format(
                                            user.getDateFormatString() + ', HH:mm',
                                        )}).`,
                                    );
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <DatePicker
                        showTime={{
                            format: 'HH:mm',
                        }}
                        format={user.getDateFormatString()}
                        placement="topLeft"
                        onChange={onChangeStartDate}
                        style={{width: '100%'}}
                    />
                </Form.Item>
                <Form.Item
                    label={'End date'}
                    name={'endDate'}
                    rules={[
                        {
                            required: true,
                            message: 'Please select an end date.',
                        },
                        {
                            validator: (_, value) => {
                                const currentDate = dayjs(new Date().getTime()).add(1, 'hour').toDate();
                                if (value && startDate && value < startDate) {
                                    return Promise.reject('End date must be after start date.');
                                }
                                if (value && value < currentDate) {
                                    return Promise.reject('End date must be in more than 1 hour from now.');
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <DatePicker
                        showTime={{
                            format: 'HH:mm',
                        }}
                        format={user.getDateFormatString()}
                        placement="topLeft"
                        style={{width: '100%'}}
                    />
                </Form.Item>
                <Form.Item label="Allow students to view results" name="showResultsOnExpiry">
                    <Radio.Group
                        optionType="button"
                        buttonStyle='solid'
                        options={[
                            {label: 'Yes', value: true},
                            {label: 'No', value: false},
                        ]}
                    />
                </Form.Item>
                <Form.Item label="Message for students" name="messageForStudents">
                    <TextArea rows={4}/>
                </Form.Item>
                <Form.Item label={'Recipient'} name={'recipient'}>
                    <Radio.Group
                        optionType="button"
                        buttonStyle='solid'
                        onChange={onRecipientChange}
                        options={[
                            {label: 'Multiple classes', value: 'MULTIPLE_CLASSES'},
                            {label: 'Single class', value: 'SINGLE_CLASS'},
                        ]}
                    />
                </Form.Item>
                {/* Classes dropdown */}
                <Form.Item
                    label={recipientState.inputLabel}
                    name="classIds"
                    rules={[
                        {
                            required: true,
                            message: 'Select at least one class.',
                        },
                        {
                            message: 'Maximum 5 classes.',
                            validator: (_, val) => {
                                if (val && val.length > 5) {
                                    return Promise.reject('You can only assign a maximum of 5 classes.');
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Select
                        onSelect={onClassChange}
                        virtual={false}
                        showSearch={false}
                        popupRender={(menu) => (
                            <>
                                {menu}
                                {recipientState.multipleDropDown && (
                                    <>
                                        <Divider style={{margin: '0 0 4px 0'}}/>
                                        <div className={'edit-assign-dialog select-checkbox-wrapper'}>
                                            <Checkbox
                                                disabled={initialClassIds && initialClassIds.length === 0}
                                                checked={initialClassIds && classIds && initialClassIds.length === classIds.length}
                                                onChange={handleSelectAllClassIds}
                                            >
                                                Select all classes
                                            </Checkbox>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        allowClear
                        autoClearSearchValue
                        onChange={handleClassChange}
                        onClear={onClassChange}
                        mode={recipientState.multipleDropDown ? 'multiple' : undefined}
                        maxTagCount="responsive"
                    >
                        {user.classes.map((clazz) => {
                            if (clazz.totalStudents && clazz.totalStudents > 0) {
                                return (
                                    <Select.Option key={clazz.classId} value={clazz.classId}>
                                        {clazz.className}
                                    </Select.Option>
                                );
                            }
                            return null;
                        })}
                    </Select>
                </Form.Item>

                {/* Students dropdown if SINGLE_CLASS */}
                {recipientState.value === 'SINGLE_CLASS' && (
                    <Form.Item
                        label="Students"
                        name="studentIds"
                        rules={[
                            {
                                required: true,
                                message: 'Select at least one student.',
                            },
                        ]}
                    >
                        <Select
                            virtual={false}
                            showSearch={false}
                            popupRender={(menu) => (
                                <>
                                    {menu}
                                    <Divider style={{margin: '0 0 4px 0'}}/>
                                    <div className={'edit-assign-dialog select-checkbox-wrapper'}>
                                        <Checkbox
                                            disabled={initialStudentIds.length === 0}
                                            checked={initialStudentIds.length === studentIds.length}
                                            onChange={handleSelectAllStudentIds}
                                        >
                                            Select all students
                                        </Checkbox>
                                    </div>
                                </>
                            )}
                            allowClear
                            autoClearSearchValue
                            onChange={handleStudentChange}
                            mode="multiple"
                            maxTagCount="responsive"
                        >
                            {students.map((student) => {
                                return (
                                    <Select.Option key={student.userId} value={student.userId}>
                                        {student.fullName}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                )}
            </Form>
        </GenericDialog>
    );
};

export default AssignDiagnosticTestDialog;
